import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import './AiCampaignCatalog.css';

const getBusinessUrl = (customWeb) => {
    if (!customWeb) return '';
    const trimmed = customWeb.trim();
    if (/^(f|ht)tps?:\/\//i.test(trimmed)) {
        return trimmed;
    }
    if (trimmed.includes('.')) {
        return `https://${trimmed}`;
    }
    return `https://${trimmed}.jiffybook.app`;
};

const getBusinessDisplayName = (customWeb) => {
    if (!customWeb) return '';
    const trimmed = customWeb.trim();
    if (/^(f|ht)tps?:\/\//i.test(trimmed)) {
        return trimmed.replace(/^(f|ht)tps?:\/\//i, '');
    }
    if (trimmed.includes('.')) {
        return trimmed;
    }
    return `${trimmed}.jiffybook.app`;
};

const AiCampaignCatalog = () => {
    const { id } = useParams();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [businessInfo, setBusinessInfo] = useState({ name: '', logo: '', customWeb: '' });
    const [loadedImages, setLoadedImages] = useState({});

    useEffect(() => {
        const fetchCatalog = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`https://us-central1-jiffybook-india.cloudfunctions.net/getCampaignCatalogData?campaignId=${id}`);
                
                const data = response.data.data || [];
                setProducts(data);

                if (data.length > 0) {
                    const customWebItem = data.find(item => item.strCustomWeb && item.strCustomWeb.trim());
                    setBusinessInfo({
                        name: data[0].strBname || 'Business Catalog',
                        logo: data[0].strBlogo || '',
                        customWeb: customWebItem ? customWebItem.strCustomWeb.trim() : ''
                    });
                }
                setLoading(false);
            } catch (err) {
                console.error('Error fetching catalog:', err);
                setError('Failed to load the catalog. Please try again later.');
                setLoading(false);
            }
        };

        if (id) {
            fetchCatalog();
        }
    }, [id]);

    const handleProductClick = (campaignId) => {
        window.location.href = `https://deal-in.jiffybook.app/product/${campaignId}`;
    };

    const handleExploreMoreClick = () => {
        window.location.href = 'https://jiffybook.app/openapp';
    };

    if (loading) {
        return (
            <div className="catalog-loader-container">
                <div className="bouncing-ball"></div>
                <p>Loading Catalog...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="catalog-error-container">
                <p>{error}</p>
                <button onClick={() => window.location.reload()} className="retry-btn">Retry</button>
            </div>
        );
    }

    return (
        <div className="catalog-page-wrapper">
            <header className="catalog-header">
                <div className="catalog-header-content">
                    {businessInfo.logo && (
                        <img src={businessInfo.logo} alt={businessInfo.name} className="business-logo" />
                    )}
                    <h1 className="business-name">{businessInfo.name}</h1>
                </div>
            </header>

            <main className="catalog-main">
                <div className="product-grid">
                    {products.map((product) => (
                        <div 
                            key={product.id} 
                            className="product-card"
                            onClick={() => handleProductClick(product.id)}
                        >
                            <div className="product-image-wrapper">
                                {!loadedImages[product.id] && (
                                    <div className="product-image-skeleton">
                                        <div className="product-skeleton-shimmer"></div>
                                    </div>
                                )}
                                <img 
                                    src={product.strImage} 
                                    alt={product.strProduct} 
                                    className={`product-image ${loadedImages[product.id] ? 'loaded' : 'loading'}`}
                                    loading="lazy"
                                    onLoad={() => setLoadedImages(prev => ({ ...prev, [product.id]: true }))}
                                />
                                
                                {product.strCustomStatus ? (
                                    <div className="image-status-badge">{product.strCustomStatus}</div>
                                ) : (
                                    <div className="image-status-badge">Deal of the Day</div>
                                )}

                                {product.numRating && (
                                    <div className="image-rating-badge">
                                        <span className="rating-star">{product.numRating} ★</span>
                                        {product.numReviewsCount && (
                                            <>
                                                <span className="rating-divider">|</span>
                                                <span className="rating-count">{product.numReviewsCount}</span>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                            
                            <div className="product-info">
                                {product.numDiscount > 0 && (
                                    <div className="offer-banner-text">
                                        {product.numDiscount}% OFF
                                    </div>
                                )}

                                <div className="product-pricing">
                                    <span className="original-price">
                                        {product.strCurrency || '₹'}&nbsp;{product.numActualCost}
                                    </span>
                                    <span className="current-price">
                                        {product.strCurrency || '₹'}&nbsp;{product.numPayment}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="explore-more-section">
                    <button className="explore-more-btn" onClick={handleExploreMoreClick}>
                        Explore More in JiffyBook App
                    </button>
                    {businessInfo.customWeb && (
                        <div className="business-url-container">
                            <a 
                                href={getBusinessUrl(businessInfo.customWeb)} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="business-url-link"
                            >
                                {getBusinessDisplayName(businessInfo.customWeb)}
                            </a>
                        </div>
                    )}
                </div>
            </main>

            <footer className="catalog-footer">
                <p>© {new Date().getFullYear()} {businessInfo.name}. Powered by JiffyBook AI.</p>
            </footer>
        </div>
    );
};

export default AiCampaignCatalog;